import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Formik, FormikActions } from 'formik';

import AccountForm from 'components/forms/AccountForm';
import PasswordForm from 'components/forms/PasswordForm';
import LocationForm from 'components/forms/LocationForm';
import Button from 'components/Button';
import { useI18n } from 'i18n/I18nContext';
import {
  DEFAULT_ONBOARD_FORM_VALUES,
  type OnboardAuthMode,
  type OnboardFormValues,
} from 'types/onboard';

export interface WizardFormProps {
  initialValues?: Partial<OnboardFormValues>;
  children: React.ReactElement[];
  onSubmit: (
    values: OnboardFormValues,
    bag: FormikActions<OnboardFormValues>,
    resetPageIndex: () => void
  ) => void;
  checkoutClaim?: boolean;
  authMode?: OnboardAuthMode;
  initialPage?: number;
  onGoogleClick?: () => void;
  googleEnabled?: boolean;
  googleLoading?: boolean;
}

function isYupValidationError(
  err: unknown
): err is { name: string; inner: Array<{ path?: string; errors: string[] }> } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name: string }).name === 'ValidationError' &&
    'inner' in err &&
    Array.isArray((err as { inner: unknown }).inner)
  );
}

function focusFirstInvalidField(errorKeys: string[]) {
  if (typeof document === 'undefined' || errorKeys.length === 0) return;
  const firstKey = errorKeys.find((key) => key !== '_form');
  if (!firstKey) return;
  const el = document.getElementById(firstKey);
  if (el && typeof (el as HTMLElement).focus === 'function') {
    (el as HTMLElement).focus();
  }
}

function FormErrorAlert({ message }: { message: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, [message]);

  return (
    <div className="gb-onboard-alert" role="alert" tabIndex={-1} ref={ref}>
      {message}
    </div>
  );
}

function schemaForPage(
  page: number,
  t: ReturnType<typeof useI18n>['t'],
  authMode: OnboardAuthMode
) {
  if (authMode === 'google') {
    return LocationForm.validationSchema(t);
  }
  if (page === 0) return AccountForm.validationSchema({}, t);
  if (page === 1) return PasswordForm.validationSchema({}, t);
  return LocationForm.validationSchema(t);
}

export default function WizardForm({
  initialValues = {} as Partial<OnboardFormValues>,
  children,
  onSubmit,
  checkoutClaim = false,
  authMode = 'password',
  initialPage = 0,
  onGoogleClick,
  googleEnabled = false,
  googleLoading = false,
}: WizardFormProps) {
  const { t } = useI18n();
  const [page, setPage] = useState(initialPage);
  const stepHeadingRef = useRef<HTMLParagraphElement>(null);
  const stepCount = children.length;

  useEffect(() => {
    setPage(initialPage);
  }, [initialPage]);

  useEffect(() => {
    stepHeadingRef.current?.focus();
  }, [page]);

  const validateForm = useCallback(
    (values: OnboardFormValues, setErrors: FormikActions<OnboardFormValues>['setErrors']) => {
      const schema = schemaForPage(page, t, authMode);

      try {
        schema.validateSync(values, { abortEarly: false });
        return true;
      } catch (err: unknown) {
        if (!isYupValidationError(err)) throw err;

        const errorList = err.inner.reduce<Record<string, string>>(
          (carry, { path: innerPath, errors }) => ({
            ...carry,
            ...(innerPath ? { [innerPath]: errors[0] } : {}),
          }),
          {}
        );

        setErrors(errorList);
        window.setTimeout(() => focusFirstInvalidField(Object.keys(errorList)), 0);
        return false;
      }
    },
    [page, t, authMode]
  );

  const handleSubmit = useCallback(
    (values: OnboardFormValues, bag: FormikActions<OnboardFormValues>) => {
      if (!validateForm(values, bag.setErrors)) return;

      const last = page === stepCount - 1;
      if (!last) {
        bag.setSubmitting(false);
        setPage((p) => Math.min(p + 1, stepCount - 1));
        return;
      }

      const resetPageIndex = () => setPage(authMode === 'google' ? stepCount - 1 : 0);
      onSubmit(values, bag, resetPageIndex);
    },
    [onSubmit, validateForm, stepCount, page, authMode]
  );

  const initialValuesMerged: OnboardFormValues = {
    ...DEFAULT_ONBOARD_FORM_VALUES,
    ...initialValues,
  };

  const childProps = {
    errors: undefined as Record<string, string> | undefined,
    t,
    onGoogleClick,
    googleEnabled,
    googleLoading,
  };

  const childrenFn = (props: typeof childProps) =>
    children.map((child) => React.cloneElement(child, { ...props }));
  const activeChild = (props: typeof childProps) => childrenFn(props)[page];

  const stepLabel = t('forms.stepProgress', {
    current: String(page + 1),
    total: String(stepCount),
  });

  const showBack = page > 0 && !(authMode === 'google' && page === stepCount - 1);

  return (
    <Formik<{}, OnboardFormValues>
      initialValues={initialValuesMerged}
      enableReinitialize
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      render={({ errors, handleSubmit: formikSubmit, isSubmitting }) => {
        const formError = (errors as Record<string, string | undefined>)._form;

        return (
          <form className="gb-onboard-form" onSubmit={formikSubmit} noValidate>
            <div
              className="gb-onboard-progress"
              style={{ ['--gb-onboard-steps' as string]: String(stepCount) }}
            >
              <div className="gb-onboard-progress__track" aria-hidden="true">
                {Array.from({ length: stepCount }, (_, index) => (
                  <span
                    key={index}
                    className="gb-onboard-progress__seg"
                    data-active={index <= page ? 'true' : 'false'}
                  />
                ))}
              </div>
              <p
                ref={stepHeadingRef}
                className="gb-onboard-progress__label outline-none"
                aria-live="polite"
                tabIndex={-1}
              >
                {stepLabel}
              </p>
            </div>

            {checkoutClaim ? (
              <p className="gb-onboard-note">{t('forms.checkoutClaimNote')}</p>
            ) : null}

            {activeChild({
              ...childProps,
              errors: errors as Record<string, string>,
            })}

            {formError ? <FormErrorAlert message={formError} /> : null}

            <div className="gb-onboard-actions">
              {showBack ? (
                <Button
                  type="button"
                  value={t('forms.back')}
                  disabled={isSubmitting}
                  className="gb-onboard-actions__secondary bg-transparent border border-white border-opacity-50 shadow-none text-white text-base font-bold p-15"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                />
              ) : null}

              {page < stepCount - 1 ? (
                <Button
                  value={t('forms.next')}
                  type="submit"
                  disabled={isSubmitting}
                  className="gb-onboard-actions__primary bg-green shadow text-white text-base font-bold p-15"
                />
              ) : (
                <Button
                  value={isSubmitting ? t('forms.creatingAccount') : t('forms.createAccount')}
                  type="submit"
                  disabled={isSubmitting}
                  className="gb-onboard-actions__primary bg-green shadow text-white text-base font-bold p-15"
                />
              )}
            </div>
          </form>
        );
      }}
    />
  );
}



// WEBPACK FOOTER //
// ./src/components/forms/WizardForm.tsx